import { AI_LEVELS, getVariant } from '@chessbut/engine';
import { RATED_BOT_AFTER_MS } from '@chessbut/shared';
import { useEffect, useState } from 'react';
import { LocalGame, saveKeyFor } from '../game/localGame.ts';
import { timeControlLabel } from '../lib/format.ts';
import { navigate } from '../lib/router.tsx';
import { track } from '../lib/track.ts';
import { copy, setShareFallback, share } from '../lib/share.ts';
import { client, useOnline } from '../online/client.ts';
import { SITE_URL } from '../lib/platform.ts';

/** Floating bar while searching for an opponent. */
export function SearchingBar() {
  const online = useOnline();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!online.queue) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [online.queue]);
  if (!online.queue) return null;
  const variant = getVariant(online.queue.variant);
  const secs = Math.max(0, Math.floor((now - online.queue.since) / 1000));
  const others = Math.max(0, (online.stats?.searching[online.queue.variant] ?? 1) - 1);
  return (
    <div className="searching-bar" role="status">
      <span className="spinner big" />
      <div>
        <b>Finding an opponent…</b>
        <p className="muted small">
          {variant?.info.name} · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
          {` · ${others === 0 ? 'nobody else' : `${others} other${others === 1 ? '' : 's'}`} in the queue · ${online.stats?.online ?? 1} online`}
        </p>
        {online.queue.bot && others === 0 && now - online.queue.since >= RATED_BOT_AFTER_MS && variant ? (
          <button
            className="text-link"
            onClick={() => {
              client.playRatedBot();
              track('queue.rated_bot', { variant: variant.id, waited: secs, level: online.queue!.bot!.level });
            }}
          >
            Nobody else is searching. Play {variant.info.bots?.[online.queue.bot.level - 1] ?? AI_LEVELS[online.queue.bot.level - 1]?.name ?? 'the computer'} ({online.queue.bot.rating}), rated →
          </button>
        ) : secs >= 20 && others === 0 && variant && (
          <button
            className="text-link"
            onClick={() => {
              client.cancelQuickMatch();
              const key = saveKeyFor({ variant: variant.id, mode: 'bot' });
              LocalGame.clear(key);
              new LocalGame({ variant: variant.id, mode: 'bot', human: Math.random() < 0.5 ? 0 : 1, level: 2 });
              track('queue.bot_fallback', { variant: variant.id, waited: secs });
              navigate(`/${variant.id}/bot`);
            }}
          >
            Quiet right now. Play the computer instead →
          </button>
        )}
      </div>
      <button className="btn small" onClick={() => client.cancelQuickMatch()}>
        Cancel
      </button>
    </div>
  );
}

/** Your open invite: code, link, share. */
export function RoomPanel() {
  const online = useOnline();
  const room = online.room;
  if (!room) return null;
  const variant = getVariant(room.variant);
  const url = `${SITE_URL}/join/${room.code}`;
  return (
    <div className="sheet-backdrop">
      <div className="sheet room" role="dialog" aria-label="Invite link">
        <div className="sheet-grip" aria-hidden />
        <div className="room-body">
          <h2>Invite ready</h2>
          <p className="muted">
            {variant?.info.name} · {timeControlLabel(room.timeControl)} · send this to a friend. The game starts when they open it.
          </p>
          <div className="room-code">{room.code}</div>
          <button
            className="btn primary big block"
            onClick={() =>
              share({
                kind: 'invite',
                title: 'ChessBut invite',
                text: `Play me in chess, but ${variant?.info.but ?? 'weird'} ${variant?.info.emoji ?? ''}`,
                url,
              })
            }
          >
            Share invite link
          </button>
          <button className="btn block" onClick={() => copy(url, 'Link copied')}>
            Copy link
          </button>
          <p className="muted small">
            <span className="spinner" /> Waiting for your friend…
          </p>
          <button className="text-link" onClick={() => client.cancelRoom()}>
            Cancel invite
          </button>
        </div>
      </div>
    </div>
  );
}

/** Shown when the server rejected something or the connection was taken over. */
export function ConnectionBanner() {
  const online = useOnline();
  if (online.status === 'replaced') {
    return (
      <div className="banner">
        ChessBut is open in another tab or device.
        <button className="btn small" onClick={() => client.restart()}>
          Play here instead
        </button>
      </div>
    );
  }
  if (online.status === 'banned') {
    return <div className="banner">This account has been suspended.</div>;
  }
  if (online.status === 'outdated') {
    return (
      <div className="banner">
        A new version of ChessBut is out.
        <button className="btn small" onClick={() => location.reload()}>
          Refresh
        </button>
      </div>
    );
  }
  return null;
}

/** Shown when neither the share sheet nor the clipboard works (some in-app browsers). */
export function ShareFallback() {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    setShareFallback(setText);
    return () => setShareFallback(null);
  }, []);
  if (text === null) return null;
  return (
    <div className="sheet-backdrop" onClick={() => setText(null)}>
      <div className="sheet room" role="dialog" aria-label="Copy" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grip" aria-hidden />
        <div className="room-body">
          <h2>Copy this</h2>
          <p className="muted">Press and hold to copy, then paste it anywhere.</p>
          <textarea className="copy-box" readOnly value={text} rows={Math.min(8, text.split('\n').length + 1)} onFocus={(e) => e.currentTarget.select()} autoFocus />
          <button className="btn block" onClick={() => setText(null)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
